<template>
  <div v-if="showBanner" class="cookie-consent" :class="{ 'cookie-consent--visible': showBanner }">
    <div class="cookie-consent__content">
      <div class="cookie-consent__text">
        <h3>Cookie-instellingen</h3>
        <p>
          Wij gebruiken cookies om uw ervaring te verbeteren en om te analyseren hoe onze website wordt gebruikt. 
          U kunt kiezen welke cookies u accepteert. Meer informatie vindt u in ons 
          <router-link to="/cookies" @click="closeBanner">cookiebeleid</router-link>.
        </p>
      </div>
      <div class="cookie-consent__actions">
        <button 
          class="cookie-consent__btn cookie-consent__btn--accept" 
          @click="acceptAll"
        >
          Alles accepteren
        </button>
        <button 
          class="cookie-consent__btn cookie-consent__btn--reject" 
          @click="rejectAll"
        >
          Weigeren
        </button>
        <button 
          class="cookie-consent__btn cookie-consent__btn--settings" 
          @click="showSettings = true"
        >
          Instellingen
        </button>
      </div>
    </div>
  </div>

  <!-- Settings Modal - Teleported to body for proper positioning -->
  <Teleport to="body">
    <div v-if="showSettings" class="cookie-settings" @click.self="showSettings = false">
      <div class="cookie-settings__modal">
        <div class="cookie-settings__header">
          <h2>Cookie-instellingen</h2>
          <button class="cookie-settings__close" @click="showSettings = false" aria-label="Sluiten">
            ×
          </button>
        </div>
        
        <div class="cookie-settings__content">
          <div class="cookie-settings__section">
            <div class="cookie-settings__toggle">
              <div class="cookie-settings__info">
                <h3>Noodzakelijke cookies</h3>
                <p>Deze cookies zijn essentieel voor het functioneren van de website en kunnen niet worden uitgeschakeld.</p>
              </div>
              <div class="cookie-settings__switch cookie-settings__switch--disabled">
                <span>Altijd actief</span>
              </div>
            </div>
          </div>

          <div class="cookie-settings__section">
            <div class="cookie-settings__toggle">
              <div class="cookie-settings__info">
                <h3>Analytische cookies</h3>
                <p>Deze cookies helpen ons te begrijpen hoe bezoekers de website gebruiken door informatie anoniem te verzamelen en te rapporteren.</p>
              </div>
              <label class="cookie-settings__switch">
                <input 
                  type="checkbox" 
                  v-model="settings.analytics"
                  @change="updateSettings"
                />
                <span class="cookie-settings__slider"></span>
              </label>
            </div>
          </div>
        </div>

        <div class="cookie-settings__footer">
          <button 
            class="cookie-settings__btn cookie-settings__btn--save" 
            @click="saveSettings"
          >
            Instellingen opslaan
          </button>
          <button 
            class="cookie-settings__btn cookie-settings__btn--cancel" 
            @click="showSettings = false"
          >
            Annuleren
          </button>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script>
import { getCookieConsent, setCookieConsent, hasConsentDecision } from '../utils/cookies'
import { initAnalytics, removeAnalytics } from '../utils/analytics'

export default {
  name: 'CookieConsent',
  data() {
    return {
      showBanner: false,
      showSettings: false,
      settings: {
        analytics: false
      }
    }
  },
  mounted() {
    // Listen for open settings event
    window.addEventListener('openCookieSettings', this.handleOpenSettings)
    
    // Only show banner if user hasn't made a decision yet
    if (!hasConsentDecision()) {
      this.showBanner = true
    } else {
      // If consent exists, initialize analytics if consented
      const consent = getCookieConsent()
      if (consent && consent.analytics) {
        initAnalytics()
      }
    }
  },
  beforeUnmount() {
    window.removeEventListener('openCookieSettings', this.handleOpenSettings)
  },
  methods: {
    acceptAll() {
      const consent = {
        analytics: true
      }
      setCookieConsent(consent)
      this.showBanner = false
      initAnalytics()
    },
    rejectAll() {
      const consent = {
        analytics: false
      }
      setCookieConsent(consent)
      this.showBanner = false
      removeAnalytics()
    },
    updateSettings() {
      // Settings updated, but not saved yet
    },
    saveSettings() {
      setCookieConsent({
        analytics: this.settings.analytics
      })
      this.showBanner = false
      this.showSettings = false
      
      if (this.settings.analytics) {
        initAnalytics()
      } else {
        removeAnalytics()
      }
    },
    closeBanner() {
      // Close banner when navigating to cookies page
      this.showBanner = false
    },
    handleOpenSettings() {
      // Show settings modal when requested from cookies page
      const consent = getCookieConsent()
      if (consent) {
        this.settings.analytics = consent.analytics || false
      }
      this.showBanner = true
      this.showSettings = true
    }
  }
}
</script>

<style scoped>
.cookie-consent {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: #fff;
  box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.1);
  z-index: 10000;
  padding: 1.5rem;
  transform: translateY(100%);
  transition: transform 0.3s ease;
}

.cookie-consent--visible {
  transform: translateY(0);
}

.cookie-consent__content {
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  gap: 2rem;
  flex-wrap: wrap;
}

.cookie-consent__text {
  flex: 1;
  min-width: 300px;
}

.cookie-consent__text h3 {
  margin: 0 0 0.5rem 0;
  font-size: 1.1rem;
  color: #2c3e50;
}

.cookie-consent__text p {
  margin: 0;
  font-size: 0.9rem;
  color: #666;
  line-height: 1.5;
}

.cookie-consent__text a {
  color: #42b983;
  text-decoration: underline;
}

.cookie-consent__actions {
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
  flex-shrink: 0;
}

.cookie-consent__btn {
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 4px;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s;
  white-space: nowrap;
  min-width: fit-content;
}

.cookie-consent__btn--accept {
  background-color: #42b983;
  color: #fff;
}

.cookie-consent__btn--accept:hover {
  background-color: #35a372;
}

.cookie-consent__btn--reject {
  background-color: #e0e0e0;
  color: #333;
}

.cookie-consent__btn--reject:hover {
  background-color: #d0d0d0;
}

.cookie-consent__btn--settings {
  background-color: transparent;
  color: #42b983;
  border: 1px solid #42b983;
}

.cookie-consent__btn--settings:hover {
  background-color: #42b983;
  color: #fff;
}

/* Settings Modal */
.cookie-settings {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.5);
  z-index: 10001;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem 1rem;
  overflow-y: auto;
  box-sizing: border-box;
  /* Ensure it's positioned relative to viewport, not parent */
  margin: 0;
  /* Inherit font from app */
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

.cookie-settings__modal {
  background: #fff;
  border-radius: 8px;
  max-width: 600px;
  width: 100%;
  max-height: calc(100vh - 4rem);
  overflow-y: auto;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
  position: relative;
  /* Ensure modal is centered */
  margin: 0 auto;
  flex-shrink: 0;
  /* Inherit font */
  font-family: inherit;
}

.cookie-settings__header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  border-bottom: 1px solid #e0e0e0;
}

.cookie-settings__header h2 {
  margin: 0;
  font-size: 1.5rem;
  color: #2c3e50;
  font-weight: 600;
  font-family: inherit;
}

.cookie-settings__close {
  background: none;
  border: none;
  font-size: 2rem;
  color: #666;
  cursor: pointer;
  padding: 0;
  width: 2rem;
  height: 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
  transition: color 0.3s;
}

.cookie-settings__close:hover {
  color: #2c3e50;
}

.cookie-settings__content {
  padding: 1.5rem;
}

.cookie-settings__section {
  margin-bottom: 2rem;
}

.cookie-settings__section:last-child {
  margin-bottom: 0;
}

.cookie-settings__toggle {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
}

.cookie-settings__info {
  flex: 1;
}

.cookie-settings__info h3 {
  margin: 0 0 0.5rem 0;
  font-size: 1.1rem;
  color: #2c3e50;
  font-weight: 600;
  font-family: inherit;
}

.cookie-settings__info p {
  margin: 0;
  font-size: 0.9rem;
  color: #666;
  line-height: 1.5;
  font-family: inherit;
}

.cookie-settings__switch {
  position: relative;
  display: inline-block;
  width: 50px;
  height: 26px;
  flex-shrink: 0;
}

.cookie-settings__switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.cookie-settings__slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #ccc;
  transition: 0.3s;
  border-radius: 26px;
}

.cookie-settings__slider:before {
  position: absolute;
  content: "";
  height: 20px;
  width: 20px;
  left: 3px;
  bottom: 3px;
  background-color: white;
  transition: 0.3s;
  border-radius: 50%;
}

.cookie-settings__switch input:checked + .cookie-settings__slider {
  background-color: #42b983;
}

.cookie-settings__switch input:checked + .cookie-settings__slider:before {
  transform: translateX(24px);
}

.cookie-settings__switch--disabled {
  display: flex;
  align-items: center;
  justify-content: center;
  width: auto;
  height: auto;
  padding: 0.5rem 1rem;
  background-color: #f5f5f5;
  border-radius: 4px;
}

.cookie-settings__switch--disabled span {
  font-size: 0.85rem;
  color: #666;
  font-family: inherit;
}

.cookie-settings__footer {
  display: flex;
  gap: 1rem;
  padding: 1.5rem;
  border-top: 1px solid #e0e0e0;
  justify-content: flex-end;
}

.cookie-settings__btn {
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 4px;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s;
  font-family: inherit;
}

.cookie-settings__btn--save {
  background-color: #42b983;
  color: #fff;
}

.cookie-settings__btn--save:hover {
  background-color: #35a372;
}

.cookie-settings__btn--cancel {
  background-color: #e0e0e0;
  color: #333;
}

.cookie-settings__btn--cancel:hover {
  background-color: #d0d0d0;
}

@media (max-width: 768px) {
  .cookie-consent__content {
    flex-direction: column;
    align-items: stretch;
  }

  .cookie-consent__actions {
    width: 100%;
  }

  .cookie-consent__btn {
    flex: 1;
    min-width: 0;
  }
}

@media (max-width: 600px) {
  .cookie-consent {
    padding: 1rem;
  }

  .cookie-consent__content {
    gap: 1rem;
  }

  .cookie-consent__text {
    min-width: 0;
  }

  .cookie-consent__actions {
    flex-direction: column;
    width: 100%;
  }

  .cookie-consent__btn {
    width: 100%;
    flex: none;
  }

  .cookie-settings {
    padding: 1rem 0.5rem;
    align-items: center;
  }

  .cookie-settings__modal {
    max-height: calc(100vh - 2rem);
    margin: auto;
  }

  .cookie-settings__toggle {
    flex-direction: column;
  }

  .cookie-settings__footer {
    flex-direction: column;
  }

  .cookie-settings__btn {
    width: 100%;
  }
}
</style>

